import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import style from './Header.module.scss'
import samsClubLogo from '../assets/icons/samsClubLogo.svg'
import locationPinIcon from '../assets/icons/howDoYouWant.svg'
import searchIcon from '../assets/icons/search.svg'
import reorderIcon from '../assets/icons/reorder.svg'
import savingsIcon from '../assets/icons/savings.svg'
import accountIcon from '../assets/icons/account.svg'
import cartIcon from '../assets/icons/cart.svg'

interface IHeaderProps {
  schema: IEcommerceQuestionSchema
  showCart: boolean
  total: number
  totalPrice: number
  findability: boolean
  clearSelection: () => void
  onCartOpen: () => void
  onHomeClick: () => void
}

const SUB_NAV_CHIPS = [
  { key: 'subNavDepartments', chevron: true },
  { key: 'subNavServices', chevron: true },
  { key: 'subNavSameDayDelivery' },
  { key: 'subNavSavings' },
  { key: 'subNavEbtEligible' },
  { key: 'subNavFallEverything' },
  { key: 'subNavEasyMealIdeas' },
  { key: 'subNavNewArrivals' },
  { key: 'subNavSamsClubCredit' },
  { key: 'subNavPharmacy' },
  { key: 'subNavSamsGarage' },
  { key: 'subNavBonusOffers' },
  { key: 'subNavSamsCashPromotions' }
]

const Header = ({
  schema,
  showCart,
  total,
  totalPrice,
  findability,
  clearSelection,
  onCartOpen,
  onHomeClick
}: IHeaderProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()

  const cartInner = (
    <>
      <span className={style.cartIconWrap}>
        <img src={cartIcon} alt="" role="presentation" className={style.cartIcon} />
        {(viewport.mobile || total > 0) && <span className={style.cartBadge}>{total > 99 ? '99+' : total}</span>}
      </span>
      <span
        className={style.cartTotal}
        dangerouslySetInnerHTML={{
          __html: formatPriceWithLocale(totalPrice, schema.price_mask, languageTag, true, false)
        }}
      />
    </>
  )
  const cart = findability ? (
    <EmptyLink color="#0a6cff">
      <div className={style.cart}>{cartInner}</div>
    </EmptyLink>
  ) : (
    <button
      type="button"
      className={style.cart}
      onClick={onCartOpen}
      aria-label={t('questions', 'ec', 'samsclub', 'cart')}
    >
      {cartInner}
    </button>
  )

  if (viewport.mobile) {
    const searchTerm = schema.search_condition
    return (
      <header className={style.headerMobile} data-testid="SW-EC-Header">
        <div className={style.mobileRow}>
          <button
            type="button"
            className={style.back}
            onClick={onHomeClick}
            aria-label={t('questions', 'ec', 'samsclub', 'back')}
            data-testid="SW-EC-Back"
          >
            <i className="ld-ChevronLeft" />
          </button>
          {showCart ? (
            <>
              <span className={style.mobileTitle}>{t('questions', 'ec', 'samsclub', 'cart')}</span>
              <span className={style.backSpacer} />
            </>
          ) : (
            <>
              <div className={style.mobileSearch} onClick={clearSelection}>
                <img src={searchIcon} alt="" role="presentation" className={style.mobileSearchIcon} />
                <span
                  className={`${style.mobileSearchText} ${searchTerm ? '' : style.mobileSearchPlaceholder}`}
                  dangerouslySetInnerHTML={{ __html: searchTerm || t('questions', 'ec', 'samsclub', 'search') }}
                />
              </div>
              {cart}
            </>
          )}
        </div>
        {!showCart && (
          <div className={style.mobileLocation}>
            <EmptyLink color="#0a6cff">
              <span className={style.mobileLocationMain}>
                <img src={locationPinIcon} alt="" role="presentation" className={style.mobileLocationPin} />
                <span className={style.mobileLocationText}>
                  {t('questions', 'ec', 'samsclub', 'howDoYouWant')}
                  <span className={style.mobileLocationZip}>| {t('questions', 'ec', 'samsclub', 'cartZip')}</span>
                </span>
              </span>
            </EmptyLink>
            <EmptyLink color="#0a6cff">
              <i className={`ld-ChevronDown ${style.mobileLocationChevron}`} />
            </EmptyLink>
          </div>
        )}
      </header>
    )
  }

  return (
    <header className={style.header} data-testid="SW-EC-Header">
      <div className={style.topBar}>
        <img
          src={samsClubLogo}
          alt={t('questions', 'ec', 'samsclub', 'store')}
          className={style.logo}
          onClick={onHomeClick}
        />
        <EmptyLink color="#0a6cff">
          <span className={style.locationPill}>
            <img src={locationPinIcon} alt="" role="presentation" className={style.locationPin} />
            <span className={style.locationText}>
              <span className={style.locationTitle}>{t('questions', 'ec', 'samsclub', 'howDoYouWant')}</span>
              <span className={style.locationLine}>{t('questions', 'ec', 'samsclub', 'locationLine')}</span>
            </span>
            <i className={`pi pi-chevron-down ${style.chevron}`} />
          </span>
        </EmptyLink>
        <div className={style.search} onClick={clearSelection}>
          <div
            className={style.searchText}
            dangerouslySetInnerHTML={{ __html: schema.search_condition || t('questions', 'ec', 'samsclub', 'search') }}
          />
          <img src={searchIcon} alt="" role="presentation" className={style.searchIcon} />
        </div>
        <div className={style.actions}>
          <EmptyLink color="#0a6cff">
            <span className={style.action}>
              <img src={reorderIcon} alt="" role="presentation" className={style.actionIcon} />
              {t('questions', 'ec', 'samsclub', 'reorder')}
            </span>
          </EmptyLink>
          <EmptyLink color="#0a6cff">
            <span className={style.action}>
              <img src={savingsIcon} alt="" role="presentation" className={style.actionIcon} />
              {t('questions', 'ec', 'samsclub', 'savings')}
            </span>
          </EmptyLink>
          <EmptyLink color="#0a6cff">
            <span className={style.action}>
              <img src={accountIcon} alt="" role="presentation" className={style.actionIcon} />
              <span className={style.accountText}>
                <span className={style.accountSignIn}>{t('questions', 'ec', 'samsclub', 'signIn')}</span>
                <span className={style.accountLabel}>{t('questions', 'ec', 'samsclub', 'account')}</span>
              </span>
            </span>
          </EmptyLink>
          {cart}
        </div>
      </div>
      <div className={style.subNav}>
        {SUB_NAV_CHIPS.map(({ key, chevron }) => (
          <EmptyLink key={key} color="#0a6cff">
            <span className={`${style.chip} ${chevron ? style.chipPrimary : ''}`}>
              {t('questions', 'ec', 'samsclub', key)}
              {chevron && <i className={`pi pi-chevron-down ${style.chipChevron}`} />}
            </span>
          </EmptyLink>
        ))}
      </div>
    </header>
  )
}

export default Header
